
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";

interface ContactSubmission {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  message: string;
  created_at: string;
}

const AdminContactSubmissions = () => {
  const { toast } = useToast();
  const [selectedMessage, setSelectedMessage] = useState<ContactSubmission | null>(null);
  
  const { data: submissions, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["contact-submissions"],
    queryFn: async () => {
      console.log("[AdminContactSubmissions] Fetching contact submissions");
      const { data, error } = await supabase
        .from("contact_submissions")
        .select("*")
        .order("created_at", { ascending: false });
      
      if (error) {
        console.error("[AdminContactSubmissions] Error:", error);
        throw error;
      }
      
      console.log("[AdminContactSubmissions] Received data:", data);
      return data as ContactSubmission[];
    },
  });

  useEffect(() => {
    const channel = supabase
      .channel('contact-submissions-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'contact_submissions'
        },
        async (payload) => {
          const newSubmission = payload.new as ContactSubmission;
          toast({
            title: "New Contact Message",
            description: `From: ${newSubmission.first_name} ${newSubmission.last_name}`,
          });
          await refetch();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [toast, refetch]);

  const openMessageDialog = (submission: ContactSubmission) => {
    setSelectedMessage(submission);
  };

  return (
    <div className="min-h-screen py-24">
      <div className="max-w-7xl mx-auto px-4">
        <h1 className="text-3xl font-bold mb-8">Contact Form Submissions</h1>
        
        {isLoading && <p className="text-gray-500">Loading submissions...</p>}
        
        {isError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
            <p>Error loading submissions: {error instanceof Error ? error.message : 'Unknown error'}</p>
          </div>
        )}
        
        {submissions && submissions.length === 0 && (
          <p className="text-gray-500 mb-6">No contact form submissions yet.</p>
        )}
        
        {submissions && submissions.length > 0 && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Message</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {submissions.map((submission) => (
                  <TableRow key={submission.id}>
                    <TableCell>
                      {new Date(submission.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {submission.first_name} {submission.last_name}
                    </TableCell>
                    <TableCell>{submission.email}</TableCell>
                    <TableCell className="max-w-md">
                      <button 
                        onClick={() => openMessageDialog(submission)}
                        className="truncate text-left w-full hover:underline text-blue-600"
                      >
                        {submission.message}
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Message Dialog */}
        <Dialog open={!!selectedMessage} onOpenChange={(open) => !open && setSelectedMessage(null)}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                Message from {selectedMessage?.first_name} {selectedMessage?.last_name}
              </DialogTitle>
              <DialogClose className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </DialogClose>
            </DialogHeader>
            <div className="mt-4 space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-500">From</p>
                <p>
                  {selectedMessage?.first_name} {selectedMessage?.last_name} ({selectedMessage?.email})
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Date</p>
                <p>
                  {selectedMessage && new Date(selectedMessage.created_at).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Message</p>
                <div className="bg-gray-50 p-4 rounded-md mt-2 whitespace-pre-wrap">
                  {selectedMessage?.message}
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminContactSubmissions;
